import {ReactElement, useCallback, useEffect, useMemo, useState} from "react";
import {
    FieldValues,
    Controller,
    useForm,
} from "react-hook-form";
import * as Yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import {Dialog} from "primereact/dialog";
import {RadioButton} from "primereact/radiobutton";
import {classNames} from "primereact/utils";
import {InputText} from "primereact/inputtext";
import {InputMask} from "primereact/inputmask";
import {Button} from "primereact/button";

type DialogProps = {
    visible: boolean;
    onClose: () => void;
    isSpouseTinSelected: boolean;
    setFieldValue: (field: string, value: string, option: any) => void;
}

function AddTinDialog(props: DialogProps): ReactElement {

    const {
        visible,
        onClose,
        setFieldValue,
        isSpouseTinSelected
    } = props;

    const [isTinSelected, setTinSelected] = useState(true);
    const yupScheme = useMemo(() => {

        return Yup.object().shape({
            applicableTin: Yup.boolean(),
            ssn: Yup.string()
                .when('applicableTin', {
                    is: (val: any) => val === true,
                    then: Yup.string().matches(/^\d{3}-\d{2}-\d{4}$/, 'TIN must be in the format XXX-XX-XXXX'),
                })
                .required('This field is required'),
        })

    }, [isSpouseTinSelected]);


    const {
        control,
        handleSubmit,
        formState: {errors},
        reset,
        setValue,
        clearErrors,
    } = useForm({
        defaultValues: {
            applicableTin: true,
            ssn: '',
        },
        resolver: yupResolver(yupScheme),
    });

    const handleClose = useCallback(() => {
        clearErrors();
        onClose();
        reset();
        setTinSelected(true);
    }, [clearErrors, reset, onClose]);

    const onSubmitHandler = useCallback((data: FieldValues) => {

        if (isSpouseTinSelected) {
            setFieldValue("spouse_id", data.ssn, {shouldValidate: true});
        } else {
            setFieldValue("taxpayer_id", data.ssn, {shouldValidate: true});
        }
        handleClose();
    }, [isSpouseTinSelected, setFieldValue, handleClose]);

    useEffect(() => {
        return () => clearErrors();
    }, []);

    return (
        <Dialog
            visible={visible}
            header="What kind of input would you like to provide?"
            onHide={handleClose} style={{width: '30vw'}}>
            <div className="flex flex-column gap-3">

                <form className="w-full flex flex-column gap-3 p-2" onSubmit={handleSubmit(onSubmitHandler)}>
                    <div className="flex flex-wrap gap-3 p-2">
                        <Controller
                            name="applicableTin"
                            control={control}
                            render={({field}) => (
                                <>
                                    <div className="flex items-center gap-1">
                                        <RadioButton
                                            inputId="USA"
                                            value={true}
                                            checked={field.value === true}
                                            onChange={() => {
                                                setValue("ssn","");
                                                field.onChange(true);
                                                setTinSelected(true)
                                            }}
                                        />
                                        <label htmlFor="USA" className="ml-2 font-medium">TIN</label>
                                    </div>

                                    <div className="flex items-center gap-1">
                                        <RadioButton
                                            inputId="N/A"
                                            value={false}
                                            checked={field.value === false}
                                            onChange={() => {
                                                setValue("ssn","");
                                                field.onChange(false);
                                                setTinSelected(false)
                                            }}
                                        />
                                        <label htmlFor="N/A" className="ml-2 font-medium">Text</label>
                                    </div>
                                </>
                            )}
                        />
                    </div>
                    <div className="flex flex-column gap-2">
                        <label htmlFor="ssn" className={classNames("font-medium", {"text-red-500": errors.ssn})}>
                            {isSpouseTinSelected ? "Spouse’s TIN" : "Taxpayer’s TIN"}
                            <span className="ml-1 text-red-300">*</span>
                        </label>
                        <Controller
                            name="ssn"
                            control={control}
                            render={({field, fieldState}) => {
                                return (
                                    <>
                                        {
                                            isTinSelected ? (
                                                <InputMask {...field}
                                                           id={field.name}
                                                           mask="999-99-9999"
                                                           placeholder="999-99-9999"
                                                           className={classNames({
                                                               'p-invalid': fieldState.invalid,
                                                               'text-red-500': fieldState.invalid,
                                                           })}/>
                                            ) : (
                                                <InputText  {...field}
                                                            id={field.name}
                                                            className={classNames({
                                                                'p-invalid': fieldState.invalid,
                                                                'text-red-500': fieldState.invalid,
                                                            })}/>
                                            )
                                        }
                                        {
                                            fieldState.invalid ? (
                                                <small className="text-red-500">
                                                    {fieldState.error?.message}
                                                </small>
                                            ) : null
                                        }
                                    </>
                                )
                            }}
                        />
                    </div>
                    <div className="flex justify-content-end">
                        <Button label="Accept" type="submit"/>
                    </div>
                </form>
            </div>
        </Dialog>
    );
}

export default AddTinDialog;